import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import intl from "react-intl-universal";

import plotUtils from "../../../../../../script/utils/plotUtils";
import commonUtils from "../../../../../../script/utils/commonUtils";

import { PlanItem } from "./PlanItem";
import { PlanWizard } from "./PlanWizard";
import { Grid } from "../../../../../components/Grid";
import { Spacer } from "../../../../../components/Spacer";
import { BoxList } from "../../../../../components/BoxList";
import { FileSelect } from "../../../../../components/FileSelect";
import { EntryContext } from "../../../../../contexts/EntryContext";

const LayerStyle = styled.div`
    display: ${props => (props.$show ? "flex" : "none")};
`;

export const PlansLayer = props => {
    const { show, wizard, plot } = props;

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;

    const plansLayer = "plansLayer";

    const [pdf, setPdf] = useState();
    const [selectedIndex, setSelectedIndex] = useState();

    useEffect(() => {
        const layer = plot.addLayer(plansLayer, 10);
        layer.setHandler("selection:created", opt => onSelect(opt.selected[0].index));
        layer.setHandler("selection:updated", opt => onSelect(opt.selected[0].index));
        layer.setHandler("object:modified", opt => {
            const { index, left, top, angle } = opt.target;
            onModify(index, left, top, angle);
        });
        layer.setHandler("mouse:down", opt => {
            if (opt?.target?.type !== "image") {
                plot.canvas.discardActiveObject();
                plot.canvas.renderAll();
                setSelectedIndex();
            };
        });
        addPlans(layer);
    }, []);

    useEffect(() => {
        if (show) {
            plot.getLayer(plansLayer).activate();
        } else {
            setSelectedIndex();
        }
    }, [show]);

    const onStartWizard = async event => {
        setSelectedIndex();
        const pdfDoc = await commonUtils.loadPdfFromFile(event.target.files[0]);
        setPdf(pdfDoc);
        props.setWizard(true);
    };

    const onCompleteWizard = async plan => {
        setPdf();
        plan.layerName = intl.get("entry.plan.plans.defaultName", { number: entry.plans.length + 1 });
        entry.plans.push(plan);
        entryContext.setEntry(entry);
        const index = entry.plans.length - 1;
        await plotUtils.addPlan(index, plan, plot.getLayer(plansLayer), plot);
        props.setWizard(false);
    };

    const onCancelWizard = () => {
        setPdf();
        props.setWizard(false);
    };

    const addPlans = async layer => {
        const promises = entry.plans.map(async (plan, index) => {
            await plotUtils.addPlan(index, plan, layer, plot);
        });
        await Promise.all(promises);
    };

    const onModify = (index, x, y, angle) => {
        const plan = entry.plans[index];
        if (plan === undefined) {
            return;
        }
        const normAngle = angle === null ? 0 : angle;
        const { x: reRotX, y: reRotY } = plot.rotatePoint(x, y, 0, 0, normAngle * -1);
        const scale = plan.metersPerPoint / plot.refScale;
        const newReferencePointX = (reRotX / scale) * -1;
        const newReferencePointY = (reRotY / scale) * -1;
        plan.rotationAngle = angle === null ? null : Number(normAngle.toFixed(0));
        plan.referencePointX = newReferencePointX;
        plan.referencePointY = newReferencePointY;
        entryContext.setEntry(entry);
    };

    const onSelect = index => {
        setSelectedIndex(index);
        if (index === undefined) {
            return;
        }
        const layer = plot.getLayer(plansLayer);
        layer.getObjects()
            .forEach(image => {
                if (image.index === index) {
                    layer.bringObjectToFront(image);
                    plot.canvas.setActiveObject(image);
                    plot.canvas.renderAll();
                }
            });
    };

    const onDelete = index => {
        const layer = plot.getLayer(plansLayer);
        const image = layer.getObjects().find(obj => obj.index === index);
        if (image !== undefined) {
            layer.removeObject(image);
            layer.getObjects().forEach((obj, objIndex) => obj.set({ index: objIndex }));
        }
        const [removedPlan] = entry.plans.splice(index, 1);
        entry.ixusSetting.ixusAreas = entry.ixusSetting.ixusAreas.filter(a => a.planKey !== removedPlan.key);
        entryContext.setEntry(entry);
    };

    return (
        <LayerStyle $show={show}>
            {!wizard &&
                <>
                    <Grid columns="1">
                        <FileSelect
                            accept=".pdf"
                            value={intl.get("entry.plan.plans.add")}
                            onSelect={onStartWizard} />
                    </Grid>
                    <Spacer variant="horizontal" size="small" />
                    {show &&
                        <BoxList
                            selectedIndex={selectedIndex}
                            onSelect={onSelect}
                            onClose={index => onDelete(index)}>
                            {entry.plans.map((plan, index) =>
                                <PlanItem
                                    key={index}
                                    plot={plot}
                                    plan={plan}
                                    index={index}
                                    onModify={onModify} />
                            )}
                        </BoxList>
                    }
                </>
            }
            {wizard &&
                <PlanWizard
                    plot={plot}
                    pdf={pdf}
                    onCancelWizard={onCancelWizard}
                    onCompleteWizard={onCompleteWizard} />
            }
        </LayerStyle>
    );
};